import { StaticImage } from 'gatsby-plugin-image'
import React, { useEffect, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'

const ringFill = keyframes`
  from { stroke-dashoffset: 75.4; }
  to   { stroke-dashoffset: 0; }
`

const TimerDot = styled.div<{ active: boolean }>`
  position: relative;
  width: 30px;
  height: 30px;
  cursor: pointer;
  flex-shrink: 0;

  .dot-inner {
    position: absolute;
    inset: 4px;
    border-radius: 50%;
    background: ${({ active }) => (active ? '#fff' : 'transparent')};
    border: 2px solid rgba(255,255,255,0.6);
    transition: background 0.25s;
  }

  svg {
    position: absolute;
    inset: 0;
    transform: rotate(-90deg);
    overflow: visible;

    circle.ring {
      fill: none;
      stroke: #fff;
      stroke-width: 2.5;
      stroke-linecap: round;
      stroke-dasharray: 75.4;
      stroke-dashoffset: 75.4;
      ${({ active }) => active && css`animation: ${ringFill} 6s linear forwards;`}
    }
  }
`

const Wrapper = styled.div`
  position: relative;
  min-height: 130vh;
  z-index: 0;
  color: #fff;
  background-color: #341644;
  margin-top: -1px;

  .content {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
    padding-top: 80px;
    display: flex;
    gap: 60px;
    justify-content: center;
    align-items: center;
  }

  .title {
    position: relative;
    max-width: 350px;
    > h2 {
      color: #fff !important;
      font-size: 1.3em;
    }
    font-size: 1.2em;
    line-height: 1.3;
    img {
      position: absolute;
      top: 0;
      right: 0;
      transform: translate(60%, -80%);
    }
  }

  .forest {
    position: absolute;
    bottom: -50px;
    left: -15vw;
    width: 130vw;


  .forest--l1 {
    position: relative;
    width: 130vw;
  }

  .forest--l2, .forest--l3 {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 130vw;
  }
    .forest--l2 {
    animation: light-fx 4s alternate-reverse infinite;
    }

    img {
      width: 100%;
      object-fit: contain;
    }

  .penguin  {
    position: absolute;
    bottom: 34%;
    left: 50%;
    width: 300px;
    height: 200px;
    transform: translate(-48%, 50%) rotate(24deg);
  }

    .butterfly-2 {
      position: absolute;
      bottom: 200px; 
      left: 300px;
      transform: rotate(40deg);
      width: 200px;
      animation: butterfly-2-hover 2s alternate-reverse infinite ease-in-out;
    }
  }
  

  .butterfly-1 {
    position: absolute;
    width: 200px;
    top: 0px;
    right: calc(50vw - 200px);
    width: 100px;
    animation: butterfly-1-hover 2s alternate-reverse infinite ease-in-out;
    img {
      width: 100%;
      object-fit: contain;
    }
  }


  @keyframes butterfly-1-hover {
  0% {
    transform: translate(0px, 0px);
  }
  100% {
    transform: translate(0px, -20px);
  }
  }

  @keyframes butterfly-2-hover {
  0% {
    transform: rotate(40deg) translate(0px, 0px);
  }
  100% {
    transform: rotate(40deg) translate(20px, -20px);
  }
  }

  @keyframes light-fx {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
    100% {
      opacity: 1;
    }
  } 

  .quotes {
    display: grid;
    z-index: 1;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    gap: 1.4em;
    align-items: center;

    > .quote:first-child {
      grid-row: 1 / 3;
      align-self: center;
    }
  }

  .quote {
    background-color: #fff;
    border-radius: 0.4em;
    padding: 2em 1.4em;
    color: #341644;
    display: flex;
    flex-direction: column;
    width: 270px;
    gap: 2.4em;

    > .quote--text {
      font-weight: bold;
      font-size: 1.2em;
      line-height: 1.3;
    }
    > .quote--author {
      font-size: 1em;
      > .quote--author-name {
        font-weight: bold;
      }
    }
  }


@media screen and (max-width: 1179px) {
    padding: 0 20px;

  .title {
    position: relative;
    max-width: 350px;
    margin: 0 auto;
    font-size: 1.2em;
      line-height: 1.2;

    img {
      position: absolute;
      top: 0;
      left: 0;
      transform: scaleX(-1) translate(60%, -50%);
    }
  }
    .content {
      flex-direction: column;
    }

    .quote {
      width: 100%;
      max-width: 500px;

      > .quote--text {
        font-size: 1.4em;
        line-height: 1.2;
      }
      > .quote--author {
        font-size: 1em;
      }
    }

    .quotes-carousel.mobile .dots {
      display: flex;
      gap: 10px;
      margin-top: 16px;
      justify-content: center;
    }

  }


`

export const ParentsV2_RecommendedByTeachers = (props: {
  title: string
  description: string
  quotes: {
    text: string
    teacher: { name: string, school: string }
  }[]
}) => {
  const [quoteIndex, setQuoteIndex] = useState(0)
  const [autoPlay, setAutoPlay] = useState(true)
  const [inView, setInView] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const quote = props.quotes[quoteIndex]

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1220px)')
    setIsMobile(mq.matches)
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.3 }
    )
    if (wrapperRef.current) observer.observe(wrapperRef.current)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!autoPlay || !inView || !isMobile) return
    const id = setInterval(() => {
      setQuoteIndex(i => (i + 1) % props.quotes.length)
    }, 6000)
    return () => clearInterval(id)
  }, [autoPlay, inView, isMobile, props.quotes.length])

  const handleDotClick = (i: number) => {
    setAutoPlay(false)
    setQuoteIndex(i)
  }

  return <Wrapper ref={wrapperRef}>

    <div className='butterfly-1'>
      <StaticImage placeholder="blurred" src="../../../images/parents-v2/Night-Butterfly-View_01_111.png" alt="" layout="constrained" width={100} objectFit="contain" />
    </div>
    <div className='forest'>
      <div className='forest--l1'>
        <StaticImage placeholder="blurred" src="../../../images/parents-v2/tree-forest-background-01.png" alt="" layout="fullWidth" objectFit="contain" />
      </div>
      <div className='forest--l2'>
        <StaticImage placeholder="blurred" src="../../../images/parents-v2/tree-forest-midground-02.png" alt="" layout="fullWidth" objectFit="contain" />
      </div>
      <div className='penguin'>
        <StaticImage placeholder="blurred" src="../../../images/parents-v2/penguin_v9.png" alt="" layout="constrained" width={300} objectFit="contain" />
      </div>
      <div className='forest--l3'>
        <StaticImage placeholder="blurred" src="../../../images/parents-v2/tree-forest-foreground-03.png" alt="" layout="fullWidth" objectFit="contain" />
      </div>

      <div className='butterfly-2'>
        <StaticImage placeholder="blurred" src="../../../images/parents-v2/Night-Butterfly-View_01_5.png" alt="" layout="constrained" width={200} objectFit="contain" />
      </div>

    </div>
    <div className='content'>
      <div className='desktop'>
        <div className='quotes'>
          {props.quotes.map((quote, i) => <div className='quote' key={i}>
            <div className='quote--text'>{quote.text}</div>
            <div className='quote--author'>
              <div className='quote--author-name'>{quote.teacher.name}</div>
              <div className='quote--author-school'>{quote.teacher.school}</div>
            </div>
          </div>)}
        </div>
      </div>
      <div className='title'>
        <h2>{props.title}</h2>
        <p>{props.description}</p>
        <img src='/images/parents-v2/yellow-splash-doodle-icon.svg' />
      </div>

      <div className='quotes-carousel mobile'>
        <div className='quote'>
          <div className='quote--text'>"{quote.text}"</div>
          <div className='quote--author'>
            <div className='quote--author-name'>{quote.teacher.name}</div>
            <div className='quote--author-school'>{quote.teacher.school}</div>
          </div>
        </div>
        <div className='dots'>
          {props.quotes.map((_, i) => (
            <TimerDot key={i} active={quoteIndex === i} onClick={() => handleDotClick(i)}>
              <div className="dot-inner" />
              <svg key={quoteIndex === i ? quoteIndex : 'idle'} viewBox="0 0 30 30">
                <circle className="ring" cx="15" cy="15" r="12" />
              </svg>
            </TimerDot>
          ))}
        </div>
      </div>
    </div>
  </Wrapper>
}
